import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type { LoadPostingView } from '@breaker/shared-types';
import type {
  CreatePostingBody,
  CancelPostingBody,
  ListPostingsQuery,
} from '@breaker/shared-schemas';

/**
 * Load board postings for the fleet. The dispatcher view is unscoped (all of
 * the fleet's postings, newest first); pass a status to narrow. Keyed under
 * ['postings', fleetId] so every mutation below busts the whole prefix.
 */
export function usePostings(
  fleetId: string | null,
  filters: Pick<ListPostingsQuery, 'status'> = {},
) {
  const status = filters.status;
  return useQuery({
    queryKey: ['postings', fleetId ?? '', 'list', status ?? 'all'] as const,
    enabled: Boolean(fleetId),
    queryFn: () => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return api<{ postings: LoadPostingView[] }>(`/v1/fleets/${fleetId}/load-postings${qs}`);
    },
    select: (data) => data.postings,
    staleTime: 15_000,
  });
}

export function usePosting(fleetId: string | null, postingId: string | null) {
  return useQuery({
    queryKey: ['postings', fleetId ?? '', 'detail', postingId ?? ''] as const,
    enabled: Boolean(fleetId && postingId),
    queryFn: () =>
      api<{ posting: LoadPostingView }>(`/v1/fleets/${fleetId}/load-postings/${postingId}`),
    select: (data) => data.posting,
    staleTime: 15_000,
  });
}

export function useCreatePosting(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePostingBody) =>
      api<{ posting: LoadPostingView }>(`/v1/fleets/${fleetId}/load-postings`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['postings', fleetId ?? ''] });
    },
  });
}

export function useCancelPosting(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postingId, body }: { postingId: string; body: CancelPostingBody }) =>
      api<{ posting: LoadPostingView }>(`/v1/fleets/${fleetId}/load-postings/${postingId}/cancel`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['postings', fleetId ?? ''] });
    },
  });
}

export function useCompletePosting(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postingId }: { postingId: string }) =>
      api<{ posting: LoadPostingView }>(`/v1/fleets/${fleetId}/load-postings/${postingId}/complete`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['postings', fleetId ?? ''] });
    },
  });
}
